import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { 
  Plus, 
  Users, 
  Phone, 
  Mail, 
  User, 
  Trash2, 
  Edit, 
  CheckCircle, 
  XCircle,
  UserCheck,
  UserX,
  Eye,
  EyeOff,
  Calendar,
  X,
  Clock,
  Search,
  BarChart3,
  Download,
  RefreshCw,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { greeterAPI } from '../../services/api';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const Greeters = () => {
  const { isDarkMode } = useAdminTheme();
  const [greeters, setGreeters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGreeter, setEditingGreeter] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [greeterBookings, setGreeterBookings] = useState({}); // Store bookings for each greeter
  const [showBookingsModal, setShowBookingsModal] = useState(false);
  const [selectedGreeterBookings, setSelectedGreeterBookings] = useState([]);
  const [selectedGreeterName, setSelectedGreeterName] = useState('');
  
  // New state for enhanced functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [selectedGreeters, setSelectedGreeters] = useState([]);
  const [stats, setStats] = useState({
    totalGreeters: 0,
    activeGreeters: 0,
    availableGreeters: 0,
    totalBookings: 0,
    averageBookingsPerGreeter: 0
  });
  const [performanceData, setPerformanceData] = useState({});
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedGreeterProfile, setSelectedGreeterProfile] = useState(null);
  
  // Table sorting state
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: ''
  });

  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Custom availability duration state
  const [customAvailabilityHours, setCustomAvailabilityHours] = useState(72);
  
  // User activity tracking (removed since auto-refresh is disabled)
  // const [lastUserActivity, setLastUserActivity] = useState(Date.now());
  const [lastFetchTime, setLastFetchTime] = useState(0);
  
  // Debounce API calls - prevent calls within 30 seconds
  const DEBOUNCE_INTERVAL = 30000; // 30 seconds

  // Fetch all greeters
  const fetchGreeters = useCallback(async () => {
    // Debounce API calls - prevent calls within 30 seconds
    const now = Date.now();
    if (now - lastFetchTime < DEBOUNCE_INTERVAL) {
      console.log('⏳ API call debounced - too soon since last fetch');
      return;
    }
    
    try {
      setIsLoading(true);
      setLastFetchTime(now);
      const response = await greeterAPI.getAllGreetersWithBookings();
      if (response.success) {
        setGreeters(response.data.greeters);
        
        // No need to fetch individual bookings - they're already included
        // Set empty bookings data since we have booking counts in the greeter objects
        const bookingsData = {};
        response.data.greeters.forEach(greeter => {
          bookingsData[greeter._id] = []; // Empty array since we only need counts
        });
        setGreeterBookings(bookingsData);
      } else {
        toast.error('Failed to fetch greeters');
      }
    } catch (error) {
      console.error('Error fetching greeters:', error);
      toast.error('Failed to fetch greeters');
    } finally {
      setIsLoading(false);
    }
  }, [lastFetchTime, DEBOUNCE_INTERVAL]);

  // Check if greeter is currently available
  const isCurrentlyAvailable = (greeter) => {
    return greeter.isActive && greeter.isAvailable && new Date() < new Date(greeter.availabilityExpiresAt);
  };

  // Calculate statistics
  const calculateStats = useCallback(() => {
    const totalGreeters = greeters.length;
    const activeGreeters = greeters.filter(g => g.isActive).length;
    const availableGreeters = greeters.filter(isCurrentlyAvailable).length;
    const totalBookings = greeters.reduce((sum, greeter) => sum + (greeter.bookingCount || 0), 0);
    const averageBookingsPerGreeter = totalGreeters > 0 ? (totalBookings / totalGreeters).toFixed(1) : 0;
    
    setStats({ totalGreeters, activeGreeters, availableGreeters, totalBookings, averageBookingsPerGreeter });
  }, [greeters]);

  // Calculate performance metrics
  const calculatePerformanceMetrics = useCallback(() => {
    const metrics = {};
    greeters.forEach(greeter => {
      const totalBookings = greeter.bookingCount || 0;
      const completedBookings = totalBookings; // Assuming all bookings are completed for now
      const completionRate = totalBookings > 0 ? 100 : 0; // Simplified for now
      
      metrics[greeter._id] = {
        totalBookings,
        completedBookings,
        completionRate: completionRate.toFixed(1),
        averageBookingValue: 0 // Simplified for now
      };
    });
    setPerformanceData(metrics);
  }, [greeters]);

  useEffect(() => {
    fetchGreeters();
  }, [fetchGreeters]);

  // Calculate stats and performance when data changes
  useEffect(() => {
    if (greeters.length > 0) {
      calculateStats();
      calculatePerformanceMetrics();
    }
  }, [greeters, calculateStats, calculatePerformanceMetrics]);

  // Real-time updates - DISABLED by default for better performance
  // Uncomment the code below if you want auto-refresh (not recommended for production)
  /*
  useEffect(() => {
    const interval = setInterval(() => {
      // Only refresh if the page is visible, not currently loading, and user hasn't been active recently
      const timeSinceLastActivity = Date.now() - lastUserActivity;
      if (!document.hidden && !isLoading && timeSinceLastActivity > 120000) { // 2 minutes of inactivity
        fetchGreeters();
      }
    }, 300000); // 5 minutes instead of 60 seconds

    return () => clearInterval(interval);
  }, [isLoading, lastUserActivity, fetchGreeters]);
  */

  // Track user activity (disabled since auto-refresh is disabled)
  /*
  useEffect(() => {
    const updateActivity = () => setLastUserActivity(Date.now());
    
    // Add event listeners for user activity
    document.addEventListener('mousedown', updateActivity);
    document.addEventListener('keypress', updateActivity);
    document.addEventListener('scroll', updateActivity);
    
    return () => {
      document.removeEventListener('mousedown', updateActivity);
      document.removeEventListener('keypress', updateActivity);
      document.removeEventListener('scroll', updateActivity);
    };
  }, []);
  */

  // Add manual refresh function
  // Manual refresh function
  const handleManualRefresh = useCallback(() => {
    setLastFetchTime(0); // Reset debounce timer
    fetchGreeters();
  }, [fetchGreeters]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Validate form
    validateForm({ ...formData, [name]: value });
  };

  // Form validation
  const validateForm = (data = formData) => {
    const errors = {};
    let isValid = true;

    // First name validation
    if (!data.firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    } else if (data.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
      isValid = false;
    }

    // Last name validation
    if (!data.lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    } else if (data.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
      isValid = false;
    }

    // Email validation
    if (!data.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Phone validation
    if (!data.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
      isValid = false;
    } else if (!/^[+]?[\d\s\-()]{10,15}$/.test(data.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid phone number';
      isValid = false;
    }

    // Password validation (only for new greeters)
    if (!editingGreeter && !data.password.trim()) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (!editingGreeter && data.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    setFormErrors(errors);
    setIsFormValid(isValid);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      toast.error('Please fix the form errors before submitting');
      return;
    }

    try {
      setIsCreating(true);
      
      if (editingGreeter) {
        // Update existing greeter
        const updateData = { ...formData };
        if (!formData.password) {
          delete updateData.password; // Don't update password unless provided
        }
        
        const response = await greeterAPI.updateGreeter(editingGreeter._id, updateData);
        if (response.success) {
          toast.success('Greeter updated successfully');
          setEditingGreeter(null);
        } else {
          toast.error(response.message || 'Failed to update greeter');
        }
      } else {
        // Create new greeter
        const response = await greeterAPI.createGreeter(formData);
        if (response.success) {
          toast.success('Greeter created successfully');
          setShowCreateForm(false);
        } else {
          toast.error(response.message || 'Failed to create greeter');
        }
      }
      
      // Reset form and refresh list
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: ''
      });
      setShowPassword(false);
      setFormErrors({});
      setIsFormValid(false);
      fetchGreeters();
      
    } catch (error) {
      console.error('Error saving greeter:', error);
      
      // Show specific error message if available
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Show validation errors
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        toast.error(`Validation failed: ${errorMessages}`);
      } else {
        toast.error('Failed to save greeter');
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Handle delete greeter
  const handleDelete = async (greeterId) => {
    if (!window.confirm('Are you sure you want to delete this greeter?')) {
      return;
    }

    try {
      const response = await greeterAPI.deleteGreeter(greeterId);
      if (response.success) {
        toast.success('Greeter deleted successfully');
        fetchGreeters();
      } else {
        toast.error(response.message || 'Failed to delete greeter');
      }
    } catch (error) {
      console.error('Error deleting greeter:', error);
      toast.error('Failed to delete greeter');
    }
  };

  // Handle toggle availability
  const handleToggleAvailability = async (greeterId, currentStatus) => {
    try {
      const response = await greeterAPI.updateGreeterAvailability(greeterId, {
        isAvailable: !currentStatus,
        hours: 72
      });
      
      if (response.success) {
        toast.success(`Greeter ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
        fetchGreeters();
      } else {
        toast.error(response.message || 'Failed to update availability');
      }
    } catch (error) {
      console.error('Error updating availability:', error);
      toast.error('Failed to update availability');
    }
  };

  // Handle edit greeter
  const handleEdit = (greeter) => {
    setEditingGreeter(greeter);
    setFormData({
      firstName: greeter.firstName,
      lastName: greeter.lastName,
      email: greeter.email,
      phoneNumber: greeter.phoneNumber,
      password: ''
    });
    setShowCreateForm(true);
  };

  // Cancel form
  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingGreeter(null);
    setShowPassword(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: ''
    });
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort greeters
  const filteredGreeters = greeters
    .filter(greeter => {
      const matchesSearch = `${greeter.firstName} ${greeter.lastName} ${greeter.email}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? greeter.isActive : !greeter.isActive);
      const matchesAvailability = availabilityFilter === 'all' || 
        (availabilityFilter === 'available' ? isCurrentlyAvailable(greeter) : !isCurrentlyAvailable(greeter));
      
      return matchesSearch && matchesStatus && matchesAvailability;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'name':
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'status':
          aValue = a.isActive ? 1 : 0;
          bValue = b.isActive ? 1 : 0;
          break;
        case 'availability':
          aValue = isCurrentlyAvailable(a) ? 1 : 0;
          bValue = isCurrentlyAvailable(b) ? 1 : 0;
          break;
        case 'bookings':
          aValue = a.bookingCount || 0;
          bValue = b.bookingCount || 0;
          break;
        case 'performance':
          aValue = parseFloat(performanceData[a._id]?.completionRate || 0);
          bValue = parseFloat(performanceData[b._id]?.completionRate || 0);
          break;
        case 'createdAt':
        default:
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredGreeters.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedGreeters = filteredGreeters.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, availabilityFilter]);

  // Handle opening bookings modal
  const handleViewBookings = async (greeter) => {
    try {
      setIsLoading(true);
      const response = await greeterAPI.getGreeterBookings(greeter._id);
      if (response.success) {
        setSelectedGreeterBookings(response.data.bookings);
        setSelectedGreeterName(`${greeter.firstName} ${greeter.lastName}`);
        setShowBookingsModal(true);
      } else {
        toast.error('Failed to fetch booking details');
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to fetch booking details');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bulk operations
  const handleBulkSelection = (greeterId) => {
    setSelectedGreeters(prev => 
      prev.includes(greeterId) 
        ? prev.filter(id => id !== greeterId)
        : [...prev, greeterId]
    );
  };

  const handleSelectAll = () => {
    if (selectedGreeters.length === filteredGreeters.length) {
      setSelectedGreeters([]);
    } else {
      setSelectedGreeters(filteredGreeters.map(g => g._id));
    }
  };

  const handleBulkAvailabilityToggle = async (isAvailable) => {
    try {
      for (const greeterId of selectedGreeters) {
        await greeterAPI.updateGreeterAvailability(greeterId, { isAvailable, hours: customAvailabilityHours });
      }
      toast.success(`${selectedGreeters.length} greeters updated`);
      fetchGreeters();
      setSelectedGreeters([]);
    } catch (error) {
      console.error('Error updating greeters:', error);
      toast.error('Failed to update greeters');
    }
  };

  // Handle profile view
  const handleViewProfile = (greeter) => {
    setSelectedGreeterProfile(greeter);
    setShowProfileModal(true);
  };

  // Handle export
  const handleExportGreeters = () => {
    const csvData = greeters.map(greeter => ({
      Name: `${greeter.firstName} ${greeter.lastName}`,
      Email: greeter.email,
      Phone: greeter.phoneNumber,
      Status: greeter.isActive ? 'Active' : 'Inactive',
      Availability: isCurrentlyAvailable(greeter) ? 'Available' : 'Unavailable',
      'Assigned Bookings': greeter.bookingCount || 0,
      'Created Date': formatDate(greeter.createdAt)
    }));
    
    // Convert to CSV
    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'greeters-report.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Greeters data exported successfully');
  };

  return (
    <div className={`max-w-8xl mx-auto px-4 sm:px-5 lg:px-6 py-6 space-y-6 transition-colors duration-300 ${
      isDarkMode ? 'bg-skyone-page-dark' : 'bg-gray-50'
    }`}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-3xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Greeters</h1>
          <p className={`mt-2 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Manage greeters who will handle customer bookings
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExportGreeters}
            className={`btn flex items-center ${isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'}`}
            disabled={greeters.length === 0}
          >
            <Download className="w-5 h-5 mr-2" />
            Export
          </button>
          <button
            onClick={handleManualRefresh}
            className={`btn flex items-center ${isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'}`}
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Greeter
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-blue-900/40' : 'bg-blue-100'
            }`}>
              <Users className={`w-6 h-6 transition-colors duration-300 ${
                isDarkMode ? 'text-blue-400' : 'text-blue-600'
              }`} />
            </div>
            <div className="ml-4">
              <p className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Total Greeters</p>
              <p className={`text-2xl font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>{stats.totalGreeters}</p>
            </div>
          </div>
        </div>

        <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-green-900/40' : 'bg-green-100'
            }`}>
              <CheckCircle className={`w-6 h-6 transition-colors duration-300 ${
                isDarkMode ? 'text-green-400' : 'text-green-600'
              }`} />
            </div>
            <div className="ml-4">
              <p className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Active Greeters</p>
              <p className={`text-2xl font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>{stats.activeGreeters}</p>
            </div>
          </div>
        </div>

        <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-emerald-900/40' : 'bg-emerald-100'
            }`}>
              <UserCheck className={`w-6 h-6 transition-colors duration-300 ${
                isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
              }`} />
            </div>
            <div className="ml-4">
              <p className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Available Now</p>
              <p className={`text-2xl font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>{stats.availableGreeters}</p>
            </div>
          </div>
        </div>

        <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-purple-900/40' : 'bg-purple-100'
            }`}>
              <Calendar className={`w-6 h-6 transition-colors duration-300 ${
                isDarkMode ? 'text-purple-400' : 'text-purple-600'
              }`} />
            </div>
            <div className="ml-4">
              <p className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Total Bookings</p>
              <p className={`text-2xl font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>{stats.totalBookings}</p>
            </div>
          </div>
        </div>

        <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-orange-900/40' : 'bg-orange-100'
            }`}>
              <BarChart3 className={`w-6 h-6 transition-colors duration-300 ${
                isDarkMode ? 'text-orange-400' : 'text-orange-600'
              }`} />
            </div>
            <div className="ml-4">
              <p className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Avg Bookings</p>
              <p className={`text-2xl font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>{stats.averageBookingsPerGreeter}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-100' : 'text-gray-900'
          }`}>
            {editingGreeter ? 'Edit Greeter' : 'Create New Greeter'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300 ${
                    formErrors.firstName 
                      ? 'border-red-300' 
                      : (isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900')
                  }`}
                  required
                />
                {formErrors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
                )}
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300 ${
                    formErrors.lastName 
                      ? 'border-red-300' 
                      : (isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900')
                  }`}
                  required
                />
                {formErrors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
                )}
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300 ${
                    formErrors.email 
                      ? 'border-red-300' 
                      : (isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900')
                  }`}
                  required
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                )}
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Phone Number *
                </label>
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={formData.phoneNumber}
                  onChange={(value) => setFormData(prev => ({ ...prev, phoneNumber: value || '' }))}
                  className={formErrors.phoneNumber ? 'phone-input-error' : 'phone-input'}
                  style={{
                    '--PhoneInput-color--focus': '#8b5cf6',
                  }}
                />
                <style>{`
                  .phone-input, .phone-input-error {
                    width: 100%;
                  }
                  .phone-input input,
                  .phone-input .PhoneInputInput,
                  .phone-input-error input,
                  .phone-input-error .PhoneInputInput {
                    background-color: ${isDarkMode ? '#374151' : '#ffffff'} !important;
                    color: ${isDarkMode ? '#f3f4f6' : '#111827'} !important;
                    border: 1px solid ${formErrors.phoneNumber ? '#ef4444' : isDarkMode ? '#4b5563' : '#d1d5db'} !important;
                    border-radius: 0.375rem !important;
                    padding: 0.5rem 0.75rem !important;
                    width: 100% !important;
                    font-size: 0.875rem !important;
                    transition: all 0.3s ease !important;
                  }
                  .phone-input input:focus,
                  .phone-input .PhoneInputInput:focus,
                  .phone-input-error input:focus,
                  .phone-input-error .PhoneInputInput:focus {
                    outline: none !important;
                    border-color: #8b5cf6 !important;
                    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2) !important;
                  }
                  .phone-input .PhoneInputCountryIcon--border,
                  .phone-input-error .PhoneInputCountryIcon--border {
                    border-radius: 0.25rem !important;
                  }
                  .phone-input .PhoneInputCountryIcon img {
                    border-radius: 0.25rem !important;
                  }
                  .phone-input-error .PhoneInputCountryIcon img {
                    border-radius: 0.25rem !important;
                  }
                  .phone-input .PhoneInputCountrySelect {
                    background-color: ${isDarkMode ? '#374151' : '#ffffff'} !important;
                    color: ${isDarkMode ? '#f3f4f6' : '#111827'} !important;
                    border-radius: 0.375rem !important;
                  }
                  .phone-input-error .PhoneInputCountrySelect {
                    background-color: ${isDarkMode ? '#374151' : '#ffffff'} !important;
                    color: ${isDarkMode ? '#f3f4f6' : '#111827'} !important;
                    border-radius: 0.375rem !important;
                  }
                `}</style>
                {formErrors.phoneNumber && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.phoneNumber}</p>
                )}
              </div>
              
              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Password {editingGreeter ? '(leave blank to keep current)' : '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300 ${
                      formErrors.password 
                        ? 'border-red-300' 
                        : (isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900')
                    }`}
                    required={!editingGreeter}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className={`h-5 w-5 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                      }`} />
                    ) : (
                      <Eye className={`h-5 w-5 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                      }`} />
                    )}
                  </button>
                </div>
                {formErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className={`btn ${isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating || !isFormValid}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Saving...' : (editingGreeter ? 'Update Greeter' : 'Create Greeter')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
      }`}>
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <input
                type="text"
                placeholder="Search greeters by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-skyone-inset-dark border-skyone-border-dark text-gray-100 placeholder-gray-500' 
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-skyone-inset-dark border-skyone-border-dark text-gray-100' 
                  : 'border-gray-300 bg-white text-gray-900'
              }`}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value)}
              className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-skyone-inset-dark border-skyone-border-dark text-gray-100' 
                  : 'border-gray-300 bg-white text-gray-900'
              }`}
            >
              <option value="all">All Availability</option>
              <option value="available">Available</option>
              <option value="unavailable">Unavailable</option>
            </select>

            <div className="flex items-center space-x-2">
              <label className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Availability Duration:</label>
              <select
                value={customAvailabilityHours}
                onChange={(e) => setCustomAvailabilityHours(Number(e.target.value))}
                className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-skyone-inset-dark border-skyone-border-dark text-gray-100' 
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
              >
                <option value={24}>24 hours</option>
                <option value={48}>48 hours</option>
                <option value={72}>72 hours</option>
                <option value={168}>1 week</option>
              </select>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedGreeters.length > 0 && (
          <div className={`mt-4 p-4 rounded-lg border transition-colors duration-300 ${
            isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center justify-between">
              <span className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-blue-300' : 'text-blue-900'
              }`}>
                {selectedGreeters.length} greeter{selectedGreeters.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleBulkAvailabilityToggle(true)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-green-900/60 text-green-200 hover:bg-green-900/80 border border-green-700' 
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  Make Available ({customAvailabilityHours}h)
                </button>
                <button
                  onClick={() => handleBulkAvailabilityToggle(false)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-red-900/60 text-red-200 hover:bg-red-900/80 border border-red-700' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  Make Unavailable
                </button>
                <button
                  onClick={() => setSelectedGreeters([])}
                  className={`px-3 py-1 text-sm rounded-md transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-skyone-inset-dark text-zinc-200 hover:bg-skyone-border-dark border border-skyone-border-dark' 
                      : 'bg-primary-700 text-white hover:bg-primary-600'
                  }`}
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Greeters List */}
      <div className={`rounded-lg shadow-sm border transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
      }`}>
        <div className={`px-6 py-4 border-b transition-colors duration-300 ${
          isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>
              All Greeters ({filteredGreeters.length})
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSelectAll}
                className={`text-sm transition-colors duration-300 ${
                  isDarkMode 
                    ? 'text-blue-400 hover:text-blue-300' 
                    : 'text-primary-600 hover:text-primary-800'
                }`}
              >
                {selectedGreeters.length === filteredGreeters.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
        </div>
        
        {isLoading ? (
          <div className="p-6 text-center">
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto transition-colors duration-300 ${
              isDarkMode ? 'border-blue-400' : 'border-primary-600'
            }`}></div>
            <p className={`mt-2 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>Loading greeters...</p>
          </div>
        ) : filteredGreeters.length === 0 ? (
          <div className={`p-6 text-center transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            <Users className={`w-12 h-12 mx-auto mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-600' : 'text-gray-300'
            }`} />
            <p>No greeters found matching your search criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y transition-colors duration-300 ${
              isDarkMode ? 'divide-skyone-border-dark' : 'divide-gray-200'
            }`}>
              <thead className={`transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
              }`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <input
                      type="checkbox"
                      checked={selectedGreeters.length === filteredGreeters.length && filteredGreeters.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors duration-300 ${
                      isDarkMode 
                        ? 'text-gray-400 hover:bg-skyone-card-dark' 
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Name
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Contact
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors duration-300 ${
                      isDarkMode 
                        ? 'text-gray-400 hover:bg-skyone-card-dark' 
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      {sortField === 'status' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors duration-300 ${
                      isDarkMode 
                        ? 'text-gray-400 hover:bg-skyone-card-dark' 
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                    onClick={() => handleSort('availability')}
                  >
                    <div className="flex items-center">
                      Availability
                      {sortField === 'availability' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors duration-300 ${
                      isDarkMode 
                        ? 'text-gray-400 hover:bg-skyone-card-dark' 
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                    onClick={() => handleSort('bookings')}
                  >
                    <div className="flex items-center">
                      Assigned Bookings
                      {sortField === 'bookings' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors duration-300 ${
                      isDarkMode 
                        ? 'text-gray-400 hover:bg-skyone-card-dark' 
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                    onClick={() => handleSort('performance')}
                  >
                    <div className="flex items-center">
                      Performance
                      {sortField === 'performance' && (
                        sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-card-dark divide-skyone-border-dark' : 'bg-white divide-gray-200'
              }`}>
                {paginatedGreeters.map((greeter) => (
                  <tr key={greeter._id} className={`transition-colors duration-300 ${
                    isDarkMode ? 'hover:bg-skyone-inset-dark' : 'hover:bg-gray-50'
                  }`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedGreeters.includes(greeter._id)}
                        onChange={() => handleBulkSelection(greeter._id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                            isDarkMode ? 'bg-blue-900/40' : 'bg-primary-100'
                          }`}>
                            <User className={`h-5 w-5 transition-colors duration-300 ${
                              isDarkMode ? 'text-blue-400' : 'text-primary-600'
                            }`} />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            {greeter.firstName} {greeter.lastName}
                          </div>
                          <div className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            ID: {greeter._id.slice(-8)}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>
                        <div className="flex items-center mb-1">
                          <Mail className={`h-4 w-4 mr-2 transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                          }`} />
                          {greeter.email}
                        </div>
                        <div className="flex items-center">
                          <Phone className={`h-4 w-4 mr-2 transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                          }`} />
                          {greeter.phoneNumber}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-300 ${
                        greeter.isActive 
                          ? (isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                          : (isDarkMode ? 'bg-red-900/40 text-red-300 border border-red-700' : 'bg-red-100 text-red-800')
                      }`}>
                        {greeter.isActive ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactive
                          </>
                        )}
                      </span>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {isCurrentlyAvailable(greeter) ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-300 ${
                            isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800'
                          }`}>
                            <UserCheck className="h-3 w-3 mr-1" />
                            Available
                          </span>
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-300 ${
                            isDarkMode ? 'bg-red-900/40 text-red-300 border border-red-700' : 'bg-red-100 text-red-800'
                          }`}>
                            <UserX className="h-3 w-3 mr-1" />
                            Unavailable
                          </span>
                        )}
                        {!isCurrentlyAvailable(greeter) && (
                          <div className={`ml-2 text-xs transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Until: {formatDate(greeter.availabilityExpiresAt)}
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-100' : 'text-gray-900'
                        }`}>
                          {greeter.bookingCount || 0}
                        </span>
                        <span className={`ml-1 text-xs transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          booking{(greeter.bookingCount || 0) !== 1 ? 's' : ''}
                        </span>
                        {(greeter.bookingCount || 0) > 0 && (
                          <button
                            onClick={() => handleViewBookings(greeter)}
                            className={`ml-2 text-xs underline transition-colors duration-300 ${
                              isDarkMode 
                                ? 'text-blue-400 hover:text-blue-300' 
                                : 'text-primary-600 hover:text-primary-800'
                            }`}
                            title="View booking details"
                          >
                            View
                          </button>
                        )}
                      </div>
                      {greeterBookings[greeter._id]?.length > 0 && (
                        <div className={`text-xs mt-1 transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Latest: {formatDate(greeterBookings[greeter._id][0]?.createdAt)}
                        </div>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="flex items-center mb-1">
                          <BarChart3 className={`h-4 w-4 mr-2 transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                          }`} />
                          <span className={`font-medium transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            {performanceData[greeter._id]?.completionRate || 0}%
                          </span>
                        </div>
                        <div className={`text-xs transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {performanceData[greeter._id]?.totalBookings || 0} bookings
                        </div>
                        <div className={`text-xs transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Avg: ${performanceData[greeter._id]?.averageBookingValue || 0}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewProfile(greeter)}
                          className={`transition-colors duration-300 ${
                            isDarkMode 
                              ? 'text-blue-400 hover:text-blue-300' 
                              : 'text-blue-600 hover:text-blue-900'
                          }`}
                          title="View profile"
                        >
                          <User className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => handleEdit(greeter)}
                          className={`transition-colors duration-300 ${
                            isDarkMode 
                              ? 'text-blue-400 hover:text-blue-300' 
                              : 'text-primary-600 hover:text-primary-900'
                          }`}
                          title="Edit greeter"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => handleToggleAvailability(greeter._id, greeter.isAvailable)}
                          className={`transition-colors duration-300 ${
                            greeter.isAvailable 
                              ? (isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-900')
                              : (isDarkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-900')
                          }`}
                          title={greeter.isAvailable ? 'Disable availability' : 'Enable availability'}
                        >
                          {greeter.isAvailable ? (
                            <UserX className="h-4 w-4" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => handleDelete(greeter._id)}
                          className={`transition-colors duration-300 ${
                            isDarkMode 
                              ? 'text-red-400 hover:text-red-300' 
                              : 'text-red-600 hover:text-red-900'
                          }`}
                          title="Delete greeter"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {filteredGreeters.length > 0 && (
          <div className={`px-6 py-4 border-t transition-colors duration-300 ${
            isDarkMode ? 'border-skyone-border-dark bg-skyone-page-dark/60' : 'border-gray-200 bg-gray-50'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredGreeters.length)} of {filteredGreeters.length} greeters
                </div>
                <div className="flex items-center space-x-2">
                  <label className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Show:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className={`px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                    isDarkMode 
                      ? 'border-skyone-border-dark text-zinc-300 hover:bg-skyone-inset-dark' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                    isDarkMode 
                      ? 'border-skyone-border-dark text-zinc-300 hover:bg-skyone-inset-dark' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    if (pageNum > totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded transition-colors duration-300 ${
                          currentPage === pageNum
                            ? 'bg-primary-600 text-white border-primary-600'
                            : (isDarkMode 
                                ? 'border-skyone-border-dark text-zinc-300 hover:bg-skyone-inset-dark' 
                                : 'border-gray-300 text-gray-700 hover:bg-gray-50')
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                    isDarkMode 
                      ? 'border-skyone-border-dark text-zinc-300 hover:bg-skyone-inset-dark' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                    isDarkMode 
                      ? 'border-skyone-border-dark text-zinc-300 hover:bg-skyone-inset-dark' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Last
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bookings Modal */}
      {showBookingsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Assigned Bookings - {selectedGreeterName}
              </h3>
              <button
                onClick={() => setShowBookingsModal(false)}
                className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {selectedGreeterBookings.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className={`w-12 h-12 mx-auto mb-4 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-600' : 'text-gray-300'
                }`} />
                <p className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>No bookings assigned to this greeter.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y transition-colors duration-300 ${
                  isDarkMode ? 'divide-skyone-border-dark' : 'divide-gray-200'
                }`}>
                  <thead className={`transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
                  }`}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Client
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Service
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Status
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-card-dark divide-skyone-border-dark' : 'bg-white divide-gray-200'
                  }`}>
                    {selectedGreeterBookings.map((booking) => (
                      <tr key={booking._id} className={`transition-colors duration-300 ${
                        isDarkMode ? 'hover:bg-skyone-inset-dark' : 'hover:bg-gray-50'
                      }`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className={`text-sm font-medium transition-colors duration-300 ${
                              isDarkMode ? 'text-gray-100' : 'text-gray-900'
                            }`}>
                              {booking.clientName}
                            </div>
                            <div className={`text-sm transition-colors duration-300 ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {booking.clientEmail}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            {booking.serviceType || 'N/A'}
                          </div>
                          {booking.description && (
                            <div className={`text-xs mt-1 max-w-xs truncate transition-colors duration-300 ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {booking.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-300 ${
                            booking.status === 'completed' 
                              ? (isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                              : booking.status === 'assigned'
                              ? (isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-800')
                              : booking.status === 'reassigned'
                              ? (isDarkMode ? 'bg-purple-900/40 text-purple-300 border border-purple-700' : 'bg-purple-100 text-purple-800')
                              : booking.status === 'unavailable'
                              ? (isDarkMode ? 'bg-red-900/40 text-red-300 border border-red-700' : 'bg-red-100 text-red-800')
                              : (isDarkMode ? 'bg-skyone-inset-dark/90 text-gray-300 border border-skyone-border-dark' : 'bg-gray-100 text-gray-800')
                          }`}>
                            {booking.status === 'completed' ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Completed
                              </>
                            ) : booking.status === 'assigned' ? (
                              <>
                                <Clock className="h-3 w-3 mr-1" />
                                Assigned
                              </>
                            ) : booking.status === 'reassigned' ? (
                              <>
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Reassigned
                              </>
                            ) : booking.status === 'unavailable' ? (
                              <>
                                <XCircle className="h-3 w-3 mr-1" />
                                Unavailable
                              </>
                            ) : (
                              <>
                                <Clock className="h-3 w-3 mr-1" />
                                {booking.status}
                              </>
                            )}
                          </span>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {formatDate(booking.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowBookingsModal(false)}
                className={`px-4 py-2 rounded-md transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-skyone-inset-dark text-zinc-200 hover:bg-skyone-border-dark border border-skyone-border-dark' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && selectedGreeterProfile && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Greeter Profile - {selectedGreeterProfile.firstName} {selectedGreeterProfile.lastName}
              </h3>
              <button
                onClick={() => setShowProfileModal(false)}
                className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div className={`rounded-lg p-4 transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
              }`}>
                <h4 className={`text-md font-semibold mb-3 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Name</label>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>{selectedGreeterProfile.firstName} {selectedGreeterProfile.lastName}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Email</label>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>{selectedGreeterProfile.email}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Phone</label>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>{selectedGreeterProfile.phoneNumber}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Status</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-300 ${
                      selectedGreeterProfile.isActive 
                        ? (isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                        : (isDarkMode ? 'bg-red-900/40 text-red-300 border border-red-700' : 'bg-red-100 text-red-800')
                    }`}>
                      {selectedGreeterProfile.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Performance Metrics */}
              <div className={`rounded-lg p-4 transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
              }`}>
                <h4 className={`text-md font-semibold mb-3 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>Performance Metrics</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className={`text-2xl font-bold transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-primary-600'
                    }`}>
                      {performanceData[selectedGreeterProfile._id]?.completionRate || 0}%
                    </div>
                    <div className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Completion Rate</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-2xl font-bold transition-colors duration-300 ${
                      isDarkMode ? 'text-green-400' : 'text-green-600'
                    }`}>
                      {performanceData[selectedGreeterProfile._id]?.totalBookings || 0}
                    </div>
                    <div className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Total Bookings</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-2xl font-bold transition-colors duration-300 ${
                      isDarkMode ? 'text-purple-400' : 'text-purple-600'
                    }`}>
                      ${performanceData[selectedGreeterProfile._id]?.averageBookingValue || 0}
                    </div>
                    <div className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Avg Booking Value</div>
                  </div>
                </div>
              </div>

              {/* Availability Status */}
              <div className={`rounded-lg p-4 transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
              }`}>
                <h4 className={`text-md font-semibold mb-3 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>Availability Status</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors duration-300 ${
                      isCurrentlyAvailable(selectedGreeterProfile)
                        ? (isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                        : (isDarkMode ? 'bg-red-900/40 text-red-300 border border-red-700' : 'bg-red-100 text-red-800')
                    }`}>
                      {isCurrentlyAvailable(selectedGreeterProfile) ? 'Available' : 'Unavailable'}
                    </span>
                    {!isCurrentlyAvailable(selectedGreeterProfile) && (
                      <p className={`text-sm mt-1 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Until: {formatDate(selectedGreeterProfile.availabilityExpiresAt)}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Created</p>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>{formatDate(selectedGreeterProfile.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className={`rounded-lg p-4 transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
              }`}>
                <h4 className={`text-md font-semibold mb-3 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>Recent Bookings</h4>
                {selectedGreeterProfile.bookingCount > 0 ? (
                  <div className="space-y-2">
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Total assigned bookings: {selectedGreeterProfile.bookingCount}
                    </p>
                    <p className={`text-xs transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      Click "View Bookings" in the table to see detailed booking information.
                    </p>
                  </div>
                ) : (
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>No bookings assigned yet.</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowProfileModal(false)}
                className={`px-4 py-2 rounded-md transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-skyone-inset-dark text-zinc-200 hover:bg-skyone-border-dark border border-skyone-border-dark' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowProfileModal(false);
                  handleEdit(selectedGreeterProfile);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Edit Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Greeters;
